import { BaseSyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';

import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import { accountLogoutSilentlyStart, selectEmailId } from '../../../redux/account';
import { selectControls } from '../../../redux/controls';
import Spinner from '../../Spinner/Spinner';
import { TextFormControl } from '../../TextFormControl/TextFormControl';
import { deleteAccountAsync, ModalProps } from '../models';
import gaEvents from '../../../shared/analyticsEvents';

export const DeleteAccountModal = ({open, onClose}: ModalProps):JSX.Element => {
  const dispatch = useDispatch();
  const formEl = useRef<HTMLFormElement>(null);

  const controls = useSelector(selectControls);
  const emailId = useSelector(selectEmailId);

  const [contentLabel, setContentLabel] = useState<string>('');
  const [emailMismatch, setEmailMismatch] = useState<boolean>(false);
  const [resultLabel, setResultLabel] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const closeModalHandler = useCallback(() => {
    setIsLoading(false);
    setResultLabel('');
    setSubmitted(false);
    setIsValid(false);
    onClose(true);
  }, [onClose]);

  const submitFormHandler = useCallback(async(event: BaseSyntheticEvent, formEl: React.RefObject<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();
    if (formEl.current && emailId) {
      const formData = new FormData(formEl.current);
      const formProps = Object.fromEntries(formData);
      const emailIdInput = formProps['email'] as string;

      if (emailId.trim() !== emailIdInput.trim()) {
        setEmailMismatch(true);
        return;
      } else {
        emailMismatch && setEmailMismatch(false);
      }

      if (
        formEl.current.checkValidity()
      ) {
        setIsLoading(true);
        setSubmitted(true);
        if (await deleteAccountAsync()) {
          dispatch(
            accountLogoutSilentlyStart()
          );
          const label = controls?.LoginDeleteSuccess?.controlText.replace(/\$EMAILID\$/, emailId);
          setResultLabel( label ? label : `Successfully deleted account: ${emailId}`);
          gaEvents.onGA4DeleteAccount();
        } else {
          setResultLabel(controls?.StandardErrorMsg?.controlText ? controls.StandardErrorMsg.controlText : 'Oops, Something went wrong');
        }
        setIsLoading(false);
      } else {
        return;
      }
    }
  },[controls, dispatch, emailId, emailMismatch]);

  useEffect(() => {
    if (controls) {
      setContentLabel(controls.LoginDelete ? controls.LoginDelete.controlText : '');
    }
  }, [controls]);


  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal"
          // onAfterOpen={}
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          contentLabel={contentLabel}
        >
          {
            !submitted || isLoading ?
              <form ref={formEl} onSubmit={(e) => submitFormHandler(e, formEl)} className='nqt-modal--container'>
                { isLoading && <Spinner/> }
                <div className="nqt-modal--header-group">
                  <header className='nqt-modal--title'>{contentLabel}</header>
                  <button className='nqt-modal--close-button'
                    type='button'
                    ref={(el) => el?.focus()}
                    onClick={() => onClose(false)}>
                    <DeleteIcon/>
                    <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                  </button>
                </div>
                <div className="nqt-modal--border">
                  <section className="nqt-acc-modal--content container">
                    <span className="row row-cols-1">
                      <span className="col nqt-acc-modal--description">
                        {
                          controls?.LoginDeleteAreYouSure?.controlText
                        }
                      </span>
                    </span>
                    <hr className="row"/>
                    <div className="nqt-modal--form-controls container-fluid">
                      <div className="row row-cols-1 gy-3">
                        {
                          emailMismatch &&
                        <div className="col nqt-modal--error">
                          <span className="nqt-modal--error-message">
                            {
                              controls?.LoginDeleteConfirm?.controlText
                            }
                          </span>
                        </div>
                        }
                        <div className="col container-fluid">
                          <TextFormControl id='acc-modal-email'
                            autoComplete="email"
                            containerClass="nqt-modal--text-input"
                            controlName='email'
                            controlType="email"
                            errorText={controls?.LoginValidEmail?.controlText}
                            headerText={controls?.LoginID?.controlText}
                            isValidCallback={() => setIsValid(!!formEl.current?.checkValidity())}
                            pattern="^[A-Za-z0-9._%+-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{1,10}$"
                            required={true}
                            setFocus={false}/>
                        </div>
                      </div>
                    </div>
                  </section>
                  <hr />
                  <div className="nqt-modal--buttons col-9 offset-3">
                    <button className="nqt-button--cancel col-4 offset-1"
                      type='button'
                      onClick={() => onClose(false)}>{controls?.ModalCancel?.controlText}</button>
                    <button className="nqt-button--submit col-6"
                      disabled={!isValid}
                      type="submit"
                      onClick={(e) => submitFormHandler(e, formEl)}>{controls?.Confirm?.controlText}</button>
                  </div>
                </div>
              </form> :
              <div className='nqt-modal--container'>
                <div className="nqt-modal--header-group">
                  <header className='nqt-modal--title'>{contentLabel}</header>
                  <button className='nqt-modal--close-button'
                    ref={(el) => el?.focus()}
                    onClick={() => closeModalHandler()}>
                    <DeleteIcon/>
                    <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                  </button>
                </div>
                <div className="nqt-modal--border">
                  <section className="nqt-modal--content">
                    <div className="nqt-modal--description">
                      {
                        resultLabel
                      }
                    </div>
                  </section>
                </div>
              </div>

          }
        </ReactModal>
      }
    </>
  );
};

export default DeleteAccountModal;